import { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FaStar, FaCheck, FaTimes, FaCommentDots } from 'react-icons/fa';
import feedbackApi from '../../../services/feedbackApi';
import { StatCard, ChartCard, Pagination, EmptyState, baseBarOptions, doughnutOptions } from '../analytics/analyticsShared';

const CATEGORY_LABELS = {
  general: 'General',
  ai_health_monitoring: 'AI Health Monitoring',
  ai_chat: 'AI Chat',
  consultation: 'Consultation',
  waste_management: 'Waste Management',
  other: 'Other',
};

function Stars({ rating }) {
  return (
    <span className="text-amber-500 inline-flex">
      {Array.from({ length: 5 }, (_, i) => (
        <FaStar key={i} className={i < rating ? '' : 'text-gray-200'} />
      ))}
    </span>
  );
}

function StatusPill({ status }) {
  const cls = {
    pending: 'bg-amber-50 text-amber-700 ring-amber-200',
    approved: 'bg-green-50 text-green-700 ring-green-200',
    rejected: 'bg-red-50 text-red-700 ring-red-200',
  }[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

// Real customer feedback and testimonials, captured in-app as customers
// submit them. This is genuine user-testing / R&D evidence — every row
// here is an actual submission with its own timestamp.
function FeedbackReview() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await feedbackApi.getAll(params);
      setFeedback(res.feedback || []);
    } catch (err) {
      /* interceptor toasts */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleStatusChange = async (id, status) => {
    try {
      await feedbackApi.updateStatus(id, status);
      setFeedback((prev) => prev.map((f) => (f._id === id ? { ...f, status } : f)));
    } catch (err) {
      /* interceptor toasts */
    }
  };

  const stats = useMemo(() => {
    const total = feedback.length;
    const avgRating = total ? (feedback.reduce((s, f) => s + f.rating, 0) / total).toFixed(1) : '—';
    const pending = feedback.filter((f) => f.status === 'pending').length;
    const consented = feedback.filter((f) => f.consentToPublish).length;
    return { total, avgRating, pending, consented };
  }, [feedback]);

  const ratingChartData = useMemo(() => {
    const counts = [1, 2, 3, 4, 5].map((r) => feedback.filter((f) => f.rating === r).length);
    return {
      labels: ['1 star', '2 stars', '3 stars', '4 stars', '5 stars'],
      datasets: [{ data: counts, backgroundColor: '#1e5c33', borderRadius: 4 }],
    };
  }, [feedback]);

  const statusChartData = useMemo(() => {
    const colors = { pending: '#d97706', approved: '#1e5c33', rejected: '#dc2626' };
    const statuses = ['pending', 'approved', 'rejected'];
    const counts = statuses.map((s) => feedback.filter((f) => f.status === s).length);
    const nonZero = statuses.map((s, i) => ({ s, c: counts[i] })).filter((x) => x.c > 0);
    return {
      labels: (nonZero.length ? nonZero : statuses.map((s) => ({ s, c: 0 }))).map((x) => x.s.charAt(0).toUpperCase() + x.s.slice(1)),
      datasets: [{ data: (nonZero.length ? nonZero : statuses.map((s) => ({ s, c: 0 }))).map((x) => x.c), backgroundColor: (nonZero.length ? nonZero : statuses.map((s) => ({ s, c: 0 }))).map((x) => colors[x.s]), borderWidth: 0 }],
    };
  }, [feedback]);

  const paged = feedback.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading feedback…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">Customer Feedback &amp; Testimonials</h1>
        <p className="text-xs text-gray-400 mt-1">
          Real submissions from customers using the app — user-testing evidence and, where consented, public testimonials.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total submissions" value={stats.total} icon={<FaCommentDots />} />
        <StatCard label="Average rating" value={stats.avgRating} icon={<FaStar />} />
        <StatCard label="Pending review" value={stats.pending} icon={<FaCommentDots />} />
        <StatCard label="Consented for public use" value={stats.consented} icon={<FaCheck />} />
      </div>

      {feedback.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Rating distribution" className="h-64">
            <div className="h-48">
              <Bar data={ratingChartData} options={baseBarOptions} />
            </div>
          </ChartCard>
          <ChartCard title="Review status breakdown" className="h-64">
            <div className="h-48">
              <Doughnut data={statusChartData} options={doughnutOptions} />
            </div>
          </ChartCard>
        </div>
      )}

      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              statusFilter === s ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {feedback.length === 0 ? (
        <EmptyState
          icon={<FaCommentDots />}
          title="No feedback yet"
          message="Once customers submit feedback through the app, it will appear here for review."
        />
      ) : (
        <ChartCard title="Submissions">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-4">Customer</th>
                  <th className="py-2 pr-4">Category</th>
                  <th className="py-2 pr-4">Rating</th>
                  <th className="py-2 pr-4">Comment</th>
                  <th className="py-2 pr-4">Consent</th>
                  <th className="py-2 pr-4">Submitted</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {paged.map((f) => (
                  <tr key={f._id} className="border-b border-gray-50 align-top">
                    <td className="py-2 pr-4">
                      <div className="font-medium text-gray-700">
                        {f.user ? `${f.user.firstName || ''} ${f.user.lastName || ''}`.trim() : 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-400">{f.user?.email}</div>
                    </td>
                    <td className="py-2 pr-4">{CATEGORY_LABELS[f.category] || f.category}</td>
                    <td className="py-2 pr-4"><Stars rating={f.rating} /></td>
                    <td className="py-2 pr-4 max-w-xs text-gray-600">{f.comment || <span className="text-gray-300">—</span>}</td>
                    <td className="py-2 pr-4">{f.consentToPublish ? 'Yes' : 'No'}</td>
                    <td className="py-2 pr-4 whitespace-nowrap text-xs text-gray-400">
                      {new Date(f.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4"><StatusPill status={f.status} /></td>
                    <td className="py-2 pr-4">
                      {f.status !== 'approved' && (
                        <button
                          onClick={() => handleStatusChange(f._id, 'approved')}
                          className="text-green-600 hover:text-green-800 mr-2"
                          title="Approve"
                        >
                          <FaCheck />
                        </button>
                      )}
                      {f.status !== 'rejected' && (
                        <button
                          onClick={() => handleStatusChange(f._id, 'rejected')}
                          className="text-red-500 hover:text-red-700"
                          title="Reject"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} setPage={setPage} total={feedback.length} pageSize={PAGE_SIZE} />
        </ChartCard>
      )}
    </div>
  );
}

export default FeedbackReview;
